import { useEffect, useState, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, parseISO } from 'date-fns';
import logo from '../../../../img/logo/Logo_FICSULLANA.png';
import { toast } from 'react-toastify';
import { getBase64FromImage } from '../../../../utilities/pdfLogo';

const CONSTANTES = {
    MARGENES: {
        PREDETERMINADO: 15,
        TABLA: 20,
        DESCARGO: 20
    },
    FUENTES: {
        TAMANIOS: {
            PEQUENIO: 6,
            NORMAL: 8,
            MEDIANO: 10,
            GRANDE: 12,
            MUY_GRANDE: 14,
            EXTRA_GRANDE: 17
        }
    },
    COLORES: {
        NEGRO: [0, 0, 0],
        GRIS_60: [60, 60, 60],
        GRIS_80: [80, 80, 80],
        GRIS_100: [100, 100, 100],
        GRIS_CLARO: [200, 200, 200],
        GRIS_MUY_CLARO: [245, 245, 245]
    },
    NOMBRES_MESES: [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ],
    INFORMACION_CUENTA: {
        CTA: '5357079899068',
        CCI: '0025350070989906838'
    },
    TABLA_PENALIDADES: [
        ['1', 'S/3.50', 'S/6.50', 'S/9.50', 'S/12.50', 'S/15.50', 'S/18.50', 'S/21.50'],
        ['2 a 3', 'S/6.50', 'S/9.50', 'S/12.50', 'S/15.50', 'S/18.50', 'S/21.50', 'S/24.50'],
        ['4 a 7', 'S/9.50', 'S/12.50', 'S/15.50', 'S/18.50', 'S/21.50', 'S/24.50', 'S/27.50'],
        ['8 a 10', 'S/12.50', 'S/15.50', 'S/18.50', 'S/21.50', 'S/24.50', 'S/27.50', 'S/30.50'],
        ['11 a 15', 'S/15.50', 'S/18.50', 'S/21.50', 'S/24.50', 'S/27.50', 'S/30.50', 'S/33.50'],
        ['16 a 30', 'S/18.50', 'S/21.50', 'S/24.50', 'S/27.50', 'S/30.50', 'S/33.50', 'S/36.50'],
        ['31 a 40', 'S/43.00', 'S/49.00', 'S/55.00', 'S/61.00', 'S/67.00', 'S/73.00', 'S/79.00'],
        ['41 a 50', 'S/53.00', 'S/59.00', 'S/65.00', 'S/71.00', 'S/77.00', 'S/83.00', 'S/89.00'],
        ['51 a 60', 'S/63.00', 'S/69.00', 'S/75.00', 'S/81.00', 'S/87.00', 'S/93.00', 'S/99.00'],
        ['61 a 90', 'S/126.00', 'S/138.00', 'S/150.00', 'S/162.00', 'S/174.00', 'S/186.00', 'S/198.00'],
        ['91 a 120', 'S/252.00', 'S/276.00', 'S/300.00', 'S/324.00', 'S/348.00', 'S/372.00', 'S/396.00'],
        ['más de 120', 'S/302.00', 'S/326.00', 'S/334.00', 'S/350.00', 'S/358.00', 'S/382.00', 'S/406.00']
    ]
};

const ReporteCronograma = ({
    cuotas,
    cliente,
    prestamo,
    fechaEmision,
    onComplete,
    totales,
    onPDFGenerated,
}) => {
    const [estaGenerando, setEstaGenerando] = useState(false);
    const datosMemoizados = useMemo(() => {
        const cuotasVigentes = (cuotas || []).filter(
            c => c.estado !== 'pagado' && c.estado !== 'Pagado'
        );

        const avales = Array.isArray(prestamo?.avales)
            ? prestamo.avales
                .filter(aval => aval?.nombre && aval.nombre.trim())
                .slice(0, 2)
            : prestamo?.aval?.nombre && prestamo.aval.nombre.trim()
                ? [prestamo.aval]
                : [];
        const primerAval = avales[0] || null;
        const segundoAval = avales[1] || null;
        const tieneAval = avales.length > 0;
        const tieneDosAvales = avales.length > 1;

        return {
            cuotasVigentes,
            totales,
            avales,
            aval: primerAval,
            primerAval,
            segundoAval,
            tieneAval,
            tieneDosAvales
        };
    }, [cuotas, totales, prestamo]);

    const estadoPrestamo = useMemo(() => {
        const listado = Array.isArray(prestamo?.estadoPrestamo)
            ? prestamo.estadoPrestamo
            : Array.isArray(prestamo?.estados)
                ? prestamo.estados
                : Array.isArray(prestamo?.estado_prestamo)
                    ? prestamo.estado_prestamo
                    : [];
        const ultimo = listado.length > 0 ? listado[listado.length - 1]?.estado : null;
        const plano = prestamo?.estado_actual || ultimo || prestamo?.estado || '';
        return (plano || '').toString().toLowerCase();
    }, [prestamo]);

    const esReprogramado = useMemo(() => {
        return prestamo?.esReprogramado === true || estadoPrestamo === 'reprogramado';
    }, [prestamo, estadoPrestamo]);

    const esConvenio = useMemo(() => {
        return prestamo?.esConvenio === true || estadoPrestamo === 'refinanciado';
    }, [prestamo, estadoPrestamo]);

    const cuotasDeConvenio = useMemo(() => {
        if (!esConvenio || !Array.isArray(cuotas)) return [];
        const esGeneradaPorConvenio = (cuota) =>
            typeof cuota?.observaciones === 'string' &&
            cuota.observaciones.toLowerCase().includes('convenio') &&
            !cuota.observaciones.toLowerCase().includes('cerrada por convenio');
        const filtradas = cuotas.filter(esGeneradaPorConvenio);
        return filtradas.length > 0 ? filtradas : [];
    }, [cuotas, esConvenio]);

    const tituloDocumento = useMemo(() => {
        if (esReprogramado) {
            return 'REPROGRAMACION DE CREDITO';
        }
        if (esConvenio) {
            return 'CONVENIO DE NEGOCIACION DIRECTA';
        }
        return 'CRONOGRAMA DE PAGOS';
    }, [esReprogramado, esConvenio]);

    const cuotasVigentes = useMemo(() => {
        if (!cuotas || cuotas.length === 0) return [];
        return cuotas.filter(c => c.estado !== 'pagado' && c.estado !== 'Pagado');
    }, [cuotas]);

    const cuotasAMostrar = useMemo(() => {
        if (!cuotas || cuotas.length === 0) return [];

        if (esReprogramado) {
            return cuotasVigentes;
        }

        if (esConvenio) {
            if (cuotasDeConvenio.length > 0) return cuotasDeConvenio;
            return cuotasVigentes;
        }

        return cuotas;
    }, [cuotas, esReprogramado, esConvenio, cuotasVigentes, cuotasDeConvenio]);

    const mostrarColumnaEstado = useMemo(() => {
        if (!cuotasAMostrar || cuotasAMostrar.length === 0) return false;
        return cuotasAMostrar.some(c => c.estado === 'pagado' || c.estado === 'Pagado');
    }, [cuotasAMostrar]);

    const formatearFecha = useCallback((cadenaFecha) => {
        if (!cadenaFecha) return '-';
        try {
            return format(parseISO(cadenaFecha), 'dd/MM/yyyy');
        } catch {
            try {
                return format(new Date(cadenaFecha), 'dd/MM/yyyy');
            } catch {
                return '-';
            }
        }
    }, []);

    const agregarPrefijoYCerosIzquierda = useCallback((id, prefijo, longitudTotal) => {
        const idStr = String(id);
        const cerosNecesarios = longitudTotal - (prefijo.length + idStr.length);
        return prefijo + '0'.repeat(Math.max(0, cerosNecesarios)) + idStr;
    }, []);

    const dividirTextoEnLineas = useCallback((texto, longitudMaxima) => {
        if (!texto) return ['-'];
        const palabras = String(texto).split(' ');
        const lineas = [];
        let lineaActual = '';

        palabras.forEach((palabra) => {
            if (lineaActual.length + palabra.length + 1 <= longitudMaxima) {
                lineaActual += (lineaActual ? ' ' : '') + palabra;
            } else {
                if (lineaActual) lineas.push(lineaActual);
                lineaActual = palabra;
            }
        });

        if (lineaActual) lineas.push(lineaActual);
        return lineas;
    }, []);

    const establecerFuenteDocumento = useCallback((doc, estilo = 'normal', tamanio = CONSTANTES.FUENTES.TAMANIOS.NORMAL) => {
        doc.setFont('helvetica', estilo);
        doc.setFontSize(tamanio);
    }, []);

    const establecerColorTexto = useCallback((doc, color) => {
        doc.setTextColor(...color);
    }, []);

    const normalizarTextoPdf = useCallback((valor) => {
        const texto = valor === null || valor === undefined ? '' : String(valor).trim();
        return texto || '-';
    }, []);

    const renderizarBloqueFirma = useCallback((doc, {
        x,
        y,
        width,
        titulo,
        persona,
        fontSize = 8.2,
    }) => {
        const altoLinea = fontSize <= 7.4 ? 3.5 : 4;
        const anchoEtiqueta = width >= 65 ? 20 : width >= 55 ? 16 : 14;
        const valorX = x + anchoEtiqueta + 2;
        const anchoValor = Math.max(18, width - anchoEtiqueta - 2);

        doc.setDrawColor(40);
        doc.setLineWidth(0.3);
        doc.line(x, y, x + width, y);

        let cursorY = y + 5;

        const dibujarCampo = (etiqueta, valor, dejarVacio = false) => {
            establecerFuenteDocumento(doc, 'bold', fontSize);
            establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
            doc.text(`${etiqueta}:`, x, cursorY);

            if (!dejarVacio) {
                establecerFuenteDocumento(doc, 'normal', fontSize);
                const lineas = doc.splitTextToSize(normalizarTextoPdf(valor), anchoValor);
                lineas.forEach((linea, indice) => {
                    doc.text(String(linea), valorX, cursorY + (indice * altoLinea));
                });
                cursorY += lineas.length * altoLinea;
            } else {
                cursorY += altoLinea;
            }
        };

        dibujarCampo(titulo, persona?.nombre);
        dibujarCampo('DNI', persona?.dni);
        dibujarCampo('DIRECCIÓN', persona?.direccion);
        dibujarCampo('TELÉFONO', persona?.telefono);
        dibujarCampo('CONTACTO', '', true);

        return cursorY;
    }, [establecerFuenteDocumento, establecerColorTexto, normalizarTextoPdf]);

    const renderizarBloquePersonaPagare = useCallback((doc, {
        x,
        y,
        width,
        titulo,
        persona,
        fontSize = 8.5,
    }) => {
        const altoLinea = fontSize <= 8.5 ? 4 : 4.5;
        let cursorY = y;

        const dibujarCampo = (etiqueta, valor) => {
            establecerFuenteDocumento(doc, 'normal', fontSize);
            establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
            const lineas = doc.splitTextToSize(
                `${etiqueta}: ${normalizarTextoPdf(valor)}`,
                Math.max(20, width)
            );
            lineas.forEach((linea, indice) => {
                doc.text(String(linea), x, cursorY + (indice * altoLinea));
            });
            cursorY += lineas.length * altoLinea + 1.5;
        };

        dibujarCampo(titulo, persona?.nombre);
        dibujarCampo('D.N.I. N°', persona?.dni);
        dibujarCampo('DOMICILIO', persona?.direccion);

        return cursorY;
    }, [establecerFuenteDocumento, establecerColorTexto, normalizarTextoPdf]);

    const agregarLogo = useCallback(async (doc, x = CONSTANTES.MARGENES.PREDETERMINADO, y = 2) => {
        try {
            const logoBase64 = await getBase64FromImage(logo);
            doc.addImage(logoBase64, 'PNG', x, y, 55, 58);

            establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
            establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);

            const logoTextoX = x + 27.5;
            doc.text('CORPORACIÓN E INVERSIONES', logoTextoX, y + 38, { align: 'center' });
            doc.text('SULLANA LA PERLA DEL CHIRA S.A.C', logoTextoX, y + 42, { align: 'center' });

            return y + 70;
        } catch (error) {
            console.warn('Error loading logo:', error);
            return y + 20;
        }
    }, [establecerFuenteDocumento, establecerColorTexto]);

    const dibujarBloquePago = useCallback((doc, inicioY) => {
        const anchoPagina = doc.internal.pageSize.getWidth();
        const margen = CONSTANTES.MARGENES.PREDETERMINADO;
        let y = inicioY;

        establecerFuenteDocumento(doc, 'normal', 7.8);
        establecerColorTexto(doc, CONSTANTES.COLORES.GRIS_60);

        doc.text(
            'Paga tu crédito en las siguiente cuentas bancarias:',
            margen, y
        );
        y += 4;
        doc.text(
            'TITULAR: CORPORACIÓN E INVERSIONES SULLANA LA PERLA DEL CHIRA S.A.C',
            margen, y
        );
        y += 2;

        const anchoCaja = (anchoPagina - 2 * margen - 5) / 2;
        const altoCaja = 18;
        const cajaY = y;

        doc.setDrawColor(...CONSTANTES.COLORES.GRIS_80);
        doc.setLineWidth(0.4);

        const caja1Centro = margen + anchoCaja / 2;
        doc.rect(margen, cajaY, anchoCaja, altoCaja);
        establecerFuenteDocumento(doc, 'bold', 9);
        establecerColorTexto(doc, [20, 20, 20]);
        doc.text('TRANSFERENCIA A CUENTA BBVA', caja1Centro, cajaY + 6, { align: 'center' });
        establecerFuenteDocumento(doc, 'normal', 9);
        doc.text('N° CTA: 0011-0274-0100057256', caja1Centro, cajaY + 11, { align: 'center' });
        doc.text('CCI: 011-274-000100057256-88', caja1Centro, cajaY + 16, { align: 'center' });

        const caja2X = margen + anchoCaja + 5;
        const caja2Centro = caja2X + anchoCaja / 2;
        doc.rect(caja2X, cajaY, anchoCaja, altoCaja);
        establecerFuenteDocumento(doc, 'bold', 9);
        doc.text('TRANSFERENCIA A LA CUENTA BCP', caja2Centro, cajaY + 6, { align: 'center' });
        establecerFuenteDocumento(doc, 'normal', 9);
        doc.text(`N° CTA: ${CONSTANTES.INFORMACION_CUENTA.CTA}`, caja2Centro, cajaY + 11, { align: 'center' });
        doc.text(`CCI: ${CONSTANTES.INFORMACION_CUENTA.CCI}`, caja2Centro, cajaY + 16, { align: 'center' });

        return cajaY + altoCaja;
    }, [establecerFuenteDocumento, establecerColorTexto]);

    const dibujarParrafoJustificado = useCallback((doc, texto, x, y, anchoMaximo, alturaLinea = 4.5, espaciadoParrafo = 1.5) => {
        const parrafos = String(texto).trim().split('\n').map(p => p.trim());
        const anchoEspacio = doc.getTextWidth(' ');
        let cursorY = y;

        parrafos.forEach(parrafo => {
            if (!parrafo) {
                cursorY += alturaLinea;
                return;
            }

            const palabras = parrafo.split(/\s+/);
            let palabrasLinea = [];
            let anchoLinea = 0;

            palabras.forEach((palabra, i) => {
                const anchoPalabra = doc.getTextWidth(palabra);

                if (palabrasLinea.length === 0) {
                    palabrasLinea.push(palabra);
                    anchoLinea = anchoPalabra;
                } else if (anchoLinea + anchoEspacio + anchoPalabra <= anchoMaximo) {
                    palabrasLinea.push(palabra);
                    anchoLinea += anchoEspacio + anchoPalabra;
                } else {
                    if (palabrasLinea.length === 1) {
                        doc.text(palabrasLinea[0], x, cursorY);
                    } else {
                        const espacios = palabrasLinea.length - 1;
                        const espacioExtra = anchoMaximo - anchoLinea;
                        const extraPorEspacio = espacioExtra / espacios;
                        let cursorX = x;

                        palabrasLinea.forEach((palabraLinea, j) => {
                            doc.text(palabraLinea, cursorX, cursorY);
                            if (j < espacios) {
                                cursorX += doc.getTextWidth(palabraLinea) + anchoEspacio + extraPorEspacio;
                            }
                        });
                    }

                    cursorY += alturaLinea;
                    palabrasLinea = [palabra];
                    anchoLinea = anchoPalabra;
                }
            });

            if (palabrasLinea.length > 0) {
                let cursorX = x;
                palabrasLinea.forEach((palabra, k) => {
                    doc.text(palabra, cursorX, cursorY);
                    if (k < palabrasLinea.length - 1) {
                        cursorX += doc.getTextWidth(palabra) + anchoEspacio;
                    }
                });
                cursorY += alturaLinea + espaciadoParrafo;
            }
        });

        return cursorY;
    }, []);

    const generarPaginaPenalidades = useCallback(async (doc) => {
        const anchoPagina = doc.internal.pageSize.getWidth();
        const margen = 18;

        await agregarLogo(doc, margen, 2);

        establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.EXTRA_GRANDE);
        establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
        doc.text('PENALIDADES', anchoPagina - margen, 35, { align: 'right' });

        establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
        establecerColorTexto(doc, CONSTANTES.COLORES.GRIS_60);
        const aviso = '"En caso de incumplimiento total del contrato, Fic Sullana podrá resolver el contrato y aplicar una penalidad donde se aplicaran las siguentes penalidades según dias y monto desembolsado"';
        doc.text(aviso, margen, 53, { maxWidth: anchoPagina - margen * 2 });

        autoTable(doc, {
            startY: 60,
            head: [
                [
                    { content: 'Días', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                    { content: 'Monto desembolsado', colSpan: 7, styles: { halign: 'center', valign: 'middle' } }
                ],
                ['300-1000', '1001-2000', '2001-3000', '3001-4000', '4001-5000', '5001-6000', '6000 a más']
            ],
            body: CONSTANTES.TABLA_PENALIDADES,
            margin: { left: margen, right: margen },
            tableWidth: 'auto',
            styles: {
                lineWidth: 0.1,
                lineColor: CONSTANTES.COLORES.NEGRO,
                halign: 'center',
                valign: 'middle'
            },
            headStyles: {
                fillColor: CONSTANTES.COLORES.GRIS_CLARO,
                textColor: CONSTANTES.COLORES.NEGRO,
                fontSize: 8,
                fontStyle: 'bold',
            },
            bodyStyles: {
                fontSize: 8,
                textColor: CONSTANTES.COLORES.NEGRO,
            },
            alternateRowStyles: {
                fillColor: CONSTANTES.COLORES.GRIS_MUY_CLARO
            },
            columnStyles: {
                0: { halign: 'center', cellWidth: 20 }
            }
        });

        const despuesTablaY = doc.lastAutoTable.finalY + 10;
        const ultimoY = dibujarBloquePago(doc, despuesTablaY);

        establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.PEQUENIO);
        establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
        doc.text('FIC SULLANA - SOLIDEZ Y CONFIANZA', anchoPagina / 2, ultimoY + 90, { align: 'center' });
    }, [agregarLogo, dibujarBloquePago, establecerFuenteDocumento, establecerColorTexto]);

    const generarPaginaPagare = useCallback((doc) => {
        const anchoPagina = doc.internal.pageSize.getWidth();
        const margen = CONSTANTES.MARGENES.PREDETERMINADO;
        let y = 20;

        establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.MUY_GRANDE);
        doc.text('PAGARÉ', anchoPagina / 2, y, { align: 'center' });

        y += 10;
        establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.MEDIANO);

        const totalTexto = prestamo?.total
            ? `S/ ${parseFloat(prestamo.total).toFixed(2)} soles`
            : '___________ soles';

        const total = prestamo?.total
            ? `S/ ${parseFloat(prestamo.total).toFixed(2)}`
            : '___________';

        const ultimaCuota = cuotas && cuotas.length > 0 ? cuotas[cuotas.length - 1] : null;

        let textoVencimiento = '_____ de __________ de ______';
        if (ultimaCuota?.fecha_vencimiento) {
            try {
                const fechaVenc = parseISO(ultimaCuota.fecha_vencimiento);
                const diaVenc = fechaVenc.getDate();
                const mesVenc = CONSTANTES.NOMBRES_MESES[fechaVenc.getMonth()];
                const anioVenc = fechaVenc.getFullYear();
                textoVencimiento = `${diaVenc} de ${mesVenc} de ${anioVenc}`;
            } catch (error) {
                console.warn('Error parsing date:', error);
            }
        }

        establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.MEDIANO);
        doc.text(`IMPORTE: ${total}`, margen, y);
        doc.text(`VENCIMIENTO: ${textoVencimiento}`, anchoPagina - margen, y, { align: 'right' });

        y += 10;
        establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
        doc.text(
            '(Estimado de conformidad con el articulo N° 10 de la Ley N° 27287 y Circular SBS N° G-0090-2001)',
            margen, y
        );

        y += 4;
        establecerFuenteDocumento(doc, 'normal', 9);

        const textoPagare = `
Mediante el presente documento, pagaremos solidariamente a la orden de CORPORACION E INVERSIONES SULLANA LA PERLA DEL CHIRA S.A.C, en la fecha de vencimiento indicada la suma de ${totalTexto}, importe que corresponde a la liquidación de las sumas que adeudo (damos) a CORPORACION E INVERSIONES SULLANA LA PERLA DEL CHIRA S.A.C, en virtud del financiamiento otorgado.

Desde la fecha de su vencimiento y hasta su total cancelación el importe de este pagaré devengara intereses compensatorios y moratorios. La constitución en mora será automática. Así mismo, hasta la fecha de su total cancelación abonaré(mos) además las comisiones y gastos que se dirigen.

Las tasas de intereses compensatorios, moratorios, así como las comisiones y gastos que me(nos) obligo(amos) a pagar según el párrafo procedente, figuran en la hoja resumen del tarifario de CORPORACION E INVERSIONES SULLANA LA PERLA DEL CHIRA S.A.C, que declaro(mos) conocer y aceptar, puede ser modificado unilateralmente por CORPORACION E INVERSIONES SULLANA LA PERLA DEL CHIRA S.A.C, siendo suficiente mecanismo de comunicación la publicación del tarifario en la oficina de CORPORACION E INVERSIONES SULLANA LA PERLA DEL CHIRA S.A.C. Las tasas aplicables a los intereses compensatorios, moratorios, así como las comisiones y gastos señalados serán las más altas que CORPORACION E INVERSIONES SULLANA LA PERLA DEL CHIRA S.A.C, tenga al momento del pago o a elección de CORPORACION E INVERSIONES SULLANA LA PERLA DEL CHIRA S.A.C, cualquiera de las tasas de intereses, comisiones que hubiera tenido vigentes desde el momento de la emisión del presente título.

Este pagaré no requerirá protesto para su ejecución. Sin embargo, el tenedor podrá protestarlo, en cuyo caso(mos) los gastos y comisiones de dicha diligencia.

Acepto(mos) las prórrogas totales o parciales que se anoten en este documento aun cuando no estén suscritas por mi(nosotros). Este pagaré podrá ser libremente transferido y negociado por el tenedor.

Este pagaré, así como las prórrogas, no constituirán novación de la obligación contenida en el contrato de la referencia; y de conformidad con el articulo N° 1233° del código civil, su emisión, prorroga o renovación no extinguirá en ningún caso la obligación principal o primitiva, aun cuando se perjudique por cualquier causa.

Este pagare tiene naturaleza mercantil y se sujeta a las disposiciones de la Ley de títulos valores, la ley de Bancos y al proceso ejecutivo señalado en el código procesal civil en su caso. Para efectos de la ejecución de este título valor señalo (mos) como mi (nuestro) domicilio consignado en la parte inferior del mismo me(nos) someto(mos) a la jurisdicción de los jueces de ____________________________________________________________.

Declaro tener capacidad y estar plenamente facultado(s) para suscribir y emitir el presente Pagaré, asumiendo en caso contrario la responsabilidad civil y/o penal a que hubiera lugar.
En el caso que cualquiera de EL(LOS) EMITENTE(S) y/o AVAL(ES) sea iletrado o tenga imposibilidad física de firmar este pagaré, se acuerda utilizar la firma de un testigo a ruego.
`;

        const anchoMaximo = anchoPagina - margen * 2;
        y = dibujarParrafoJustificado(doc, textoPagare, margen, y, anchoMaximo, 4.5, 0);
        y += 2;

        let fecha = new Date();
        if (prestamo?.fecha_inicio) {
            try {
                fecha = parseISO(prestamo.fecha_inicio);
                if (isNaN(fecha)) throw new Error('invalid');
            } catch {
                try {
                    fecha = new Date(prestamo.fecha_inicio);
                } catch (error) {
                    console.warn('Error parsing date:', error);
                }
            }
        }

        const dia = fecha.getDate();
        const mes = CONSTANTES.NOMBRES_MESES[fecha.getMonth()] || '';
        const anio = fecha.getFullYear();

        y += 2;
        doc.text(
            `Emitido en la Ciudad de Querecotillo a los ${dia} días del mes de ${mes} del ${anio}`,
            margen, y
        );

        if (datosMemoizados.tieneDosAvales) {
            y += 10;
            const separacion = 5;
            const anchoBloque = (anchoPagina - (margen * 2) - (separacion * 2)) / 3;
            const bloques = [
                {
                    titulo: 'TITULAR',
                    firma: 'FIRMA TITULAR',
                    persona: {
                        nombre: cliente?.nombreCompleto,
                        dni: cliente?.dni,
                        direccion: cliente?.direccion,
                    },
                },
                {
                    titulo: 'AVAL 1',
                    firma: 'FIRMA AVAL 1',
                    persona: datosMemoizados.primerAval,
                },
                {
                    titulo: 'AVAL 2',
                    firma: 'FIRMA AVAL 2',
                    persona: datosMemoizados.segundoAval,
                },
            ];

            const finales = bloques.map((bloque, indice) => {
                const bloqueX = margen + (indice * (anchoBloque + separacion));
                return renderizarBloquePersonaPagare(doc, {
                    x: bloqueX,
                    y,
                    width: anchoBloque,
                    titulo: bloque.titulo,
                    persona: bloque.persona,
                });
            });

            const firmaY = Math.max(...finales) + 18;
            const anchoFirma = anchoBloque - 4;

            establecerFuenteDocumento(doc, 'normal', 8.5);
            bloques.forEach((bloque, indice) => {
                const bloqueX = margen + (indice * (anchoBloque + separacion));
                doc.line(bloqueX, firmaY, bloqueX + anchoFirma, firmaY);
                doc.text(bloque.firma, bloqueX + (anchoFirma / 2), firmaY + 5, { align: 'center' });
            });

            return;
        }

        y += 10;
        doc.text(`TITULAR: ${cliente?.nombreCompleto || ''}`, margen, y);
        y += 6;
        doc.text(`D.N.I. N°: ${cliente?.dni || ''}`, margen, y);
        y += 6;
        doc.text(`DOMICILIO: ${cliente?.direccion || ''}`, margen, y);

        if (datosMemoizados.tieneAval) {
            y += 10;
            doc.text(`AVAL: ${datosMemoizados.primerAval.nombre}`, margen, y);
            y += 6;
            doc.text(`D.N.I. N°: ${datosMemoizados.primerAval.dni || ''}`, margen, y);
            y += 6;
            doc.text(`DOMICILIO: ${datosMemoizados.primerAval.direccion || ''}`, margen, y);
        }

        y += 23;
        const anchoFirma = 60;
        const firmaY = y;

        doc.line(margen, firmaY, margen + anchoFirma, firmaY);
        doc.text('FIRMA TITULAR', margen + anchoFirma / 2, firmaY + 5, { align: 'center' });

        if (datosMemoizados.tieneAval) {
            const avalX = anchoPagina / 2 + margen;
            doc.line(avalX, firmaY, avalX + anchoFirma, firmaY);
            doc.text('FIRMA DEL AVAL', avalX + anchoFirma / 2, firmaY + 5, { align: 'center' });
        }
    }, [cuotas, prestamo, cliente, datosMemoizados, dibujarParrafoJustificado, establecerFuenteDocumento, renderizarBloquePersonaPagare]);

    const generarPDF = useCallback(async () => {
        setEstaGenerando(true);

        try {
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
            const anchoPagina = doc.internal.pageSize.getWidth();

            // ===== PÁGINA 1: CRONOGRAMA =====
            let y = await agregarLogo(doc);

            // Títulos
            const textoX = anchoPagina - CONSTANTES.MARGENES.PREDETERMINADO;
            const encabezadoY = 27;
            establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.GRANDE);
            establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
            doc.text(tituloDocumento, textoX, encabezadoY + 6, { align: 'right' });
            y = encabezadoY + 26;

            const col1X = CONSTANTES.MARGENES.PREDETERMINADO;
            const col2X = anchoPagina / 2 + 5;

            const datosIzquierda = [
                { etiqueta: 'FECHA DESEMBOLSO', valor: formatearFecha(prestamo?.fecha_generacion) },
                { etiqueta: 'FECHA INICIO', valor: formatearFecha(prestamo?.fecha_inicio) },
                { etiqueta: 'CÓD. TITULAR', valor: cliente?.idCliente || '-' },
                { etiqueta: 'TITULAR', valor: cliente?.nombreCompleto || '-' },
                { etiqueta: 'DNI', valor: cliente?.dni || '-' },
                { etiqueta: 'DIRECCIÓN', valor: cliente?.direccion || '-' },
                { etiqueta: 'PRODUCTO', valor: prestamo?.producto || '-' },
            ];

            if (datosMemoizados.tieneDosAvales) {
                datosIzquierda.splice(
                    6,
                    0,
                    { etiqueta: 'AVAL 1', valor: datosMemoizados.primerAval.nombre },
                    { etiqueta: 'AVAL 2', valor: datosMemoizados.segundoAval.nombre }
                );
            } else if (datosMemoizados.tieneAval) {
                datosIzquierda.splice(6, 0, { etiqueta: 'AVAL', valor: datosMemoizados.primerAval.nombre });
            }

            const datosDerecha = [
                { etiqueta: 'ANALISTA', valor: prestamo?.asesor || '-' },
                {
                    etiqueta: 'CÓD. PRÉSTAMO',
                    valor: prestamo?.idPrestamo
                        ? agregarPrefijoYCerosIzquierda(prestamo.idPrestamo, '79', 10)
                        : '-',
                },
                { etiqueta: 'MONEDA', valor: prestamo?.moneda || 'SOLES' },
                { etiqueta: 'MONTO', valor: prestamo?.monto ? `S/ ${parseFloat(prestamo.monto).toFixed(2)}` : '-' },
                { etiqueta: 'PERIODICIDAD', valor: prestamo?.frecuencia?.toUpperCase() || '-' },
                { etiqueta: 'N° CUOTAS', valor: prestamo?.cuotas },
                {
                    etiqueta: 'TASA DE INTERÉS',
                    valor: prestamo?.interes && prestamo?.cuotas
                        ? `${(prestamo.interes / prestamo.cuotas).toFixed(2)}%`
                        : prestamo?.interes ? `${prestamo.interes}%` : '-',
                },
                { etiqueta: 'TOTAL A PAGAR', valor: prestamo?.total ? `S/ ${parseFloat(prestamo.total).toFixed(2)}` : '-' },
            ];

            const renderizarDatosColumna = (datos, colX, inicioY) => {
                let posY = inicioY;
                datos.forEach(({ etiqueta, valor }) => {
                    establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
                    establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
                    doc.text(`${etiqueta}:`, colX, posY);
                    establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);

                    if ((etiqueta === 'DIRECCIÓN' || etiqueta === 'TITULAR' || etiqueta.startsWith('AVAL')) && valor) {
                        const lineas = dividirTextoEnLineas(valor, 33);
                        lineas.forEach((linea) => {
                            doc.text(linea, colX + 35, posY);
                            posY += 6;
                        });
                    } else {
                        doc.text(String(valor), colX + 35, posY);
                        posY += 6;
                    }
                });
                return posY;
            };

            const izquierdaY = renderizarDatosColumna(datosIzquierda, col1X, y);
            const derechaY = renderizarDatosColumna(datosDerecha, col2X, y);
            y = Math.max(izquierdaY, derechaY) + 3;

            const encabezados = mostrarColumnaEstado
                ? ['N°', 'F. VENC.', 'CAPITAL', 'INTERÉS', 'OTROS', 'CUOTA', 'ESTADO']
                : ['N°', 'F. VENC.', 'CAPITAL', 'INTERÉS', 'OTROS', 'CUOTA'];

            const filas = cuotasAMostrar.map((c, i) => {
                const fila = [
                    String(c.numero_cuota || i + 1),
                    formatearFecha(c.fecha_vencimiento),
                    String(c.capital ? Number(c.capital).toFixed(2) : '0.00'),
                    String(c.interes ? Number(c.interes).toFixed(2) : '0.00'),
                    String(
                        c.otros !== null && c.otros !== undefined
                            ? Number(c.otros).toFixed(2)
                            : (Number(c.monto) - Number(c.capital) - Number(c.interes)).toFixed(2)
                    ),
                    String(c.monto ? Number(c.monto).toFixed(2) : '0.00'),
                ];

                if (mostrarColumnaEstado) {
                    fila.push(c.estado === 'pagado' || c.estado === 'Pagado' ? 'PAGADO' : 'PENDIENTE');
                }

                return fila;
            });

            const filaTotales = [
                '',
                'TOTALES',
                totales.capital.toFixed(2),
                totales.interes.toFixed(2),
                totales.otros.toFixed(2),
                totales.cuota.toFixed(2)
            ];

            if (mostrarColumnaEstado) {
                filaTotales.push('');
            }

            filas.push(filaTotales);

            autoTable(doc, {
                startY: y,
                head: [encabezados],
                body: filas,
                margin: { left: CONSTANTES.MARGENES.TABLA, right: CONSTANTES.MARGENES.TABLA },
                tableWidth: 'auto',
                headStyles: {
                    fillColor: [230, 230, 230],
                    textColor: CONSTANTES.COLORES.NEGRO,
                    fontSize: 8,
                    fontStyle: 'bold',
                    halign: 'center',
                },
                bodyStyles: {
                    fontSize: 8,
                    textColor: CONSTANTES.COLORES.NEGRO,
                    halign: 'center',
                },
                alternateRowStyles: {
                    fillColor: CONSTANTES.COLORES.GRIS_MUY_CLARO,
                },
                columnStyles: mostrarColumnaEstado ? {
                    0: { halign: 'center' },
                    1: { halign: 'center' },
                    2: { halign: 'center' },
                    3: { halign: 'center' },
                    4: { halign: 'center' },
                    5: { halign: 'center' },
                    6: { halign: 'center' },
                } : {
                    0: { halign: 'center' },
                    1: { halign: 'center' },
                    2: { halign: 'center' },
                    3: { halign: 'center' },
                    4: { halign: 'center' },
                    5: { halign: 'center' },
                },
                didParseCell: function (datos) {
                    if (datos.row.index === filas.length - 1) {
                        datos.cell.styles.fontStyle = 'bold';
                        datos.cell.styles.fillColor = CONSTANTES.COLORES.GRIS_CLARO;
                    }
                },
            });

            y = doc.lastAutoTable.finalY + 5;

            const margenDescargo = CONSTANTES.MARGENES.DESCARGO;
            const anchoDescargo = anchoPagina - 2 * margenDescargo;
            establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
            establecerColorTexto(doc, [70, 70, 70]);
            const descargo = 'EL (LOS) CLIENTE(S) declara(n) expresamente que previamente a la celebración del contrato han recibido la información sobre las condiciones de su crédito (Tasa de interés compensatorio, comisiones y penalidades); copia de título valor, contrato de crédito por lo que firma en señal de conformidad.';
            doc.text(descargo, margenDescargo, y, { maxWidth: anchoDescargo });

            y += 30;

            const bloquesFirma = [
                {
                    titulo: 'TITULAR',
                    persona: {
                        nombre: cliente?.nombreCompleto,
                        dni: cliente?.dni,
                        direccion: cliente?.direccion,
                        telefono: cliente?.telefono,
                    },
                },
            ];

            if (datosMemoizados.tieneDosAvales) {
                bloquesFirma.push(
                    { titulo: 'AVAL 1', persona: datosMemoizados.primerAval },
                    { titulo: 'AVAL 2', persona: datosMemoizados.segundoAval }
                );
            } else if (datosMemoizados.tieneAval) {
                bloquesFirma.push({ titulo: 'AVAL', persona: datosMemoizados.primerAval });
            }

            const lineaFirmaY = y + 5;
            const separacionFirmas = bloquesFirma.length === 3 ? 5 : 10;
            const anchoFirma = bloquesFirma.length === 3
                ? (anchoPagina - (CONSTANTES.MARGENES.PREDETERMINADO * 2) - (separacionFirmas * 2)) / 3
                : 70;
            const tamañoFuenteFirma = bloquesFirma.length === 3 ? 7.2 : 8.2;
            const posicionesX = bloquesFirma.length === 3
                ? [
                    CONSTANTES.MARGENES.PREDETERMINADO,
                    CONSTANTES.MARGENES.PREDETERMINADO + anchoFirma + separacionFirmas,
                    CONSTANTES.MARGENES.PREDETERMINADO + ((anchoFirma + separacionFirmas) * 2),
                ]
                : [
                    CONSTANTES.MARGENES.PREDETERMINADO,
                    anchoPagina / 2 + CONSTANTES.MARGENES.PREDETERMINADO,
                ];

            const finalesFirmas = bloquesFirma.map((bloque, indice) =>
                renderizarBloqueFirma(doc, {
                    x: posicionesX[indice],
                    y: lineaFirmaY,
                    width: anchoFirma,
                    titulo: bloque.titulo,
                    persona: bloque.persona,
                    fontSize: tamañoFuenteFirma,
                })
            );

            y = Math.max(...finalesFirmas) + 5;

            const ultimoYHoja1 = dibujarBloquePago(doc, y);
            establecerFuenteDocumento(doc, 'italic', 7);
            establecerColorTexto(doc, CONSTANTES.COLORES.GRIS_100);
            doc.text(
                `Fecha de emisión: ${formatearFecha(fechaEmision || new Date())}`,
                anchoPagina / 2,
                ultimoYHoja1 + 8,
                { align: 'center' }
            );

            doc.addPage();
            await generarPaginaPenalidades(doc);

            doc.addPage();
            generarPaginaPagare(doc);

            const generarNombrePDF = () => {
                const nombreCliente = cliente?.nombreCompleto || 'Cliente';
                const idPrestamo = prestamo?.idPrestamo || 'SIN_ID';

                let fechaInicioPrestamo = 'SIN_FECHA';
                if (prestamo?.fecha_inicio) {
                    try {
                        const fecha = parseISO(prestamo.fecha_inicio);
                        fechaInicioPrestamo = format(fecha, 'yyyy-MM-dd');
                    } catch {
                        try {
                            const fecha = new Date(prestamo.fecha_inicio);
                            if (!isNaN(fecha)) {
                                fechaInicioPrestamo = format(fecha, 'yyyy-MM-dd');
                            }
                        } catch (error) {
                            console.warn('Error parsing fecha_inicio:', error);
                        }
                    }
                }

                const prefijo = tituloDocumento.replace(/\s+/g, '_');
                return `${prefijo}_${nombreCliente}_${idPrestamo}_${fechaInicioPrestamo}.pdf`;
            };

            const nombreArchivo = generarNombrePDF();
            const blobPdf = doc.output('blob');

            if (onPDFGenerated) {
                const urlPdf = URL.createObjectURL(blobPdf);
                onPDFGenerated(urlPdf, nombreArchivo, blobPdf);
            } else {
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blobPdf);
                link.download = nombreArchivo;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
            }
            if (onComplete) onComplete();

        } catch (error) {
            console.error('Error generando PDF:', error);
            toast.error('Error generando PDF');
            if (onComplete) onComplete();
        } finally {
            setEstaGenerando(false);
        }
    }, [
        cliente,
        prestamo,
        fechaEmision,
        datosMemoizados,
        formatearFecha,
        agregarPrefijoYCerosIzquierda,
        dividirTextoEnLineas,
        agregarLogo,
        dibujarBloquePago,
        generarPaginaPenalidades,
        generarPaginaPagare,
        establecerFuenteDocumento,
        establecerColorTexto,
        renderizarBloqueFirma,
        onPDFGenerated,
        onComplete,
        tituloDocumento,
        cuotasAMostrar,
        mostrarColumnaEstado,
        totales,
    ]);

    useEffect(() => {
        generarPDF();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return estaGenerando ? <div>Generando PDF...</div> : null;
};

export default ReporteCronograma;
